package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import kotlinx.coroutines.launch
import kotlinx.datetime.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ServicesScreen(
    onBack: () -> Unit,
    onNavigateToAddEdit: (Service?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var services by remember { mutableStateOf<List<Service>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedTab by remember { mutableStateOf(0) } // 0: Open, 1: Closed, 2: Deleted
    var serviceToDelete by remember { mutableStateOf<Service?>(null) }

    fun loadServices() {
        scope.launch {
            isLoading = true
            repository.getServices().onSuccess { list ->
                services = list.sortedBy { it.serviceDateTime }
            }.onFailure {
                services = emptyList()
            }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) {
        loadServices()
    }

    val openServices = services.filter { it.isDeleted != 1 && it.completedDate.isNullOrBlank() && it.isDone == 0 }
    val closedServices = services.filter { it.isDeleted != 1 && (!it.completedDate.isNullOrBlank() || it.isDone == 1) }
    val deletedServices = services.filter { it.isDeleted == 1 }

    val currentList = when (selectedTab) {
        0 -> openServices
        1 -> closedServices
        else -> deletedServices
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Services & Appointments") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    ToolbarPlusButton(onClick = { onNavigateToAddEdit(null) })
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                TabRow(selectedTabIndex = selectedTab) {
                    Tab(selected = selectedTab == 0, onClick = { selectedTab = 0 }, text = { Text("Open (${openServices.size})") })
                    Tab(selected = selectedTab == 1, onClick = { selectedTab = 1 }, text = { Text("Closed (${closedServices.size})") })
                    Tab(selected = selectedTab == 2, onClick = { selectedTab = 2 }, text = { Text("Deleted (${deletedServices.size})") })
                }

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (currentList.isEmpty()) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            Text("No services found in this tab.", style = MaterialTheme.typography.bodyLarge, color = MaterialTheme.colorScheme.outline)
                            if (selectedTab == 0) {
                                Button(onClick = { onNavigateToAddEdit(null) }) {
                                    Text("Create First Service")
                                }
                            }
                        }
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxWidth().weight(1f),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(items = currentList, key = { it.id }) { service ->
                            ServiceItemCard(
                                service = service,
                                onEdit = { onNavigateToAddEdit(service) },
                                onComplete = {
                                    scope.launch {
                                        val todayStr = getCurrentLocalDate().toString()
                                        val req = SaveServiceRequest(
                                            id = service.id,
                                            title = service.title,
                                            description = service.description,
                                            serviceDateTime = service.serviceDateTime,
                                            isDone = 1,
                                            completionNotes = service.completionNotes,
                                            outstandingNotes = service.outstandingNotes,
                                            completedDate = todayStr,
                                            isDeleted = 0,
                                            images = service.images
                                        )
                                        repository.saveService(req).onSuccess { loadServices() }
                                    }
                                },
                                onDelete = { serviceToDelete = service },
                                onRestore = {
                                    scope.launch {
                                        val req = SaveServiceRequest(
                                            id = service.id,
                                            title = service.title,
                                            description = service.description,
                                            serviceDateTime = service.serviceDateTime,
                                            isDone = 0,
                                            completionNotes = service.completionNotes,
                                            outstandingNotes = service.outstandingNotes,
                                            completedDate = null,
                                            isDeleted = 0,
                                            images = service.images
                                        )
                                        repository.saveService(req).onSuccess { loadServices() }
                                    }
                                }
                            )
                        }
                    }
                }
            }
        }
    }

    serviceToDelete?.let { service ->
        ConfirmDeleteDialog(
            message = "Delete '${service.title}'?",
            onConfirm = {
                val toDelete = service
                serviceToDelete = null
                scope.launch {
                    val req = SaveServiceRequest(
                        id = toDelete.id,
                        title = toDelete.title,
                        description = toDelete.description,
                        serviceDateTime = toDelete.serviceDateTime,
                        isDone = toDelete.isDone,
                        completionNotes = toDelete.completionNotes,
                        outstandingNotes = toDelete.outstandingNotes,
                        completedDate = toDelete.completedDate,
                        isDeleted = 1,
                        images = toDelete.images
                    )
                    repository.saveService(req).onSuccess { loadServices() }
                }
            },
            onDismiss = { serviceToDelete = null }
        )
    }
}

@Composable
fun ServiceItemCard(
    service: Service,
    onEdit: () -> Unit,
    onComplete: () -> Unit,
    onDelete: () -> Unit,
    onRestore: () -> Unit
) {
    val isClosed = service.isDone == 1 || !service.completedDate.isNullOrBlank()
    val isDeleted = service.isDeleted == 1

    Card(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onEdit),
        shape = MaterialTheme.shapes.medium,
        colors = CardDefaults.cardColors(
            containerColor = when {
                isDeleted -> MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
                isClosed -> successContainerColor()
                else -> MaterialTheme.colorScheme.surface
            }
        ),
        border = BorderStroke(1.dp, when {
            isDeleted -> MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
            isClosed -> successContentColor()
            else -> MaterialTheme.colorScheme.outline.copy(alpha = 0.3f)
        })
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = service.title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = "Scheduled: ${service.serviceDateTime}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.secondary
                )
                if (!service.description.isNullOrBlank()) {
                    Text(
                        text = service.description,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.outline
                    )
                }
                if (isClosed && !service.completedDate.isNullOrBlank()) {
                    Text(
                        text = "Completed: ${service.completedDate}",
                        style = MaterialTheme.typography.labelSmall,
                        color = successContentColor(),
                        fontWeight = FontWeight.Bold
                    )
                }
            }

            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                if (isDeleted) {
                    TextButton(onClick = onRestore, contentPadding = PaddingValues(4.dp)) {
                        Text("Restore", style = MaterialTheme.typography.labelSmall)
                    }
                } else if (isClosed) {
                    TextButton(onClick = onRestore, contentPadding = PaddingValues(4.dp)) {
                        Text("Reopen", style = MaterialTheme.typography.labelSmall)
                    }
                    IconButton(onClick = onDelete) {
                        CloseIcon(tint = MaterialTheme.colorScheme.error)
                    }
                } else {
                    Button(onClick = onComplete, shape = MaterialTheme.shapes.small, contentPadding = PaddingValues(horizontal = 8.dp, vertical = 2.dp)) {
                        Text("Complete", style = MaterialTheme.typography.labelSmall)
                    }
                    IconButton(onClick = onDelete) {
                        CloseIcon(tint = MaterialTheme.colorScheme.error)
                    }
                }
            }
        }
    }
}
